import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DRAWING_HEIGHT,
  DRAWING_WIDTH,
  type Stroke,
  type StrokeDoc,
  type StrokePoint,
} from '../../domain/types';
import { drawStroke, emptyStrokeDoc, exportPng, renderStrokes } from '../../lib/drawing';

const PEN_COLORS = ['#111111', '#C0392B', '#2C6FB5', '#2E8B57', '#D4A017', '#FFFFFF'];
const PEN_SIZES = [1, 3, 6, 12, 24];
const ERASER_SIZES = [8, 16, 32, 64];
const MAX_HISTORY = 50;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
/** キャンバス1枚あたりの画素数の上限。端末のメモリを食い潰さないための歯止め。 */
const MAX_CANVAS_PX = 6_000_000;
/** 入力の間引き（論理px）。これ未満の移動は捨てて、重ね描きによるムラを防ぐ。 */
const MIN_STEP = 0.6;
/** 手ぶれ・デジタイザのノイズを抑える一次フィルタの係数。 */
const SMOOTH = 0.45;

/**
 * 描画解像度。画面の画素密度に合わせ、拡大中はさらに上げる。
 * 1倍で描いて表示時に拡大すると、ペンを離した瞬間に線がぼやける。
 */
function renderScaleFor(zoom: number): number {
  const dpr = window.devicePixelRatio || 1;
  let s = Math.min(dpr * Math.min(Math.max(zoom, 1), 2), 3);
  while (s > 1 && DRAWING_WIDTH * s * DRAWING_HEIGHT * s > MAX_CANVAS_PX) s -= 0.25;
  return Math.max(1, Math.round(s * 4) / 4);
}

type Tool = 'pen' | 'eraser';

export function DrawingCanvas({
  initial,
  onDone,
  onCancel,
}: {
  initial?: StrokeDoc | null;
  onDone: (doc: StrokeDoc, png: Blob, size: { width: number; height: number }) => void;
  onCancel: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const committedRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>(initial ? [...initial.strokes] : []);
  const undoRef = useRef<Stroke[][]>([]);
  const redoRef = useRef<Stroke[][]>([]);

  const drawingRef = useRef<{ pointerId: number; stroke: Stroke; startedAt: number } | null>(null);
  const hasSeenPenRef = useRef(false);
  const pinchRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStartRef = useRef<{ dist: number; zoom: number; cx: number; cy: number; panX: number; panY: number } | null>(null);
  const panRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const spaceRef = useRef(false);

  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(PEN_COLORS[0]);
  const [penSize, setPenSize] = useState(3);
  const [eraserSize, setEraserSize] = useState(16);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [, forceRender] = useState(0);
  const [busy, setBusy] = useState(false);
  const [renderScale, setRenderScale] = useState(() => renderScaleFor(1));
  /** 平滑化の途中状態（直前の出力座標と筆圧）。 */
  const smoothRef = useRef<{ x: number; y: number; p: number } | null>(null);

  const bg = initial?.background ?? '#FFFFFF';

  /** 確定済みストロークを保持する裏キャンバス。毎回の全再描画を避ける。 */
  const ensureCommitted = useCallback(() => {
    const w = Math.round(DRAWING_WIDTH * renderScale);
    const h = Math.round(DRAWING_HEIGHT * renderScale);
    let c = committedRef.current;
    if (!c) {
      c = document.createElement('canvas');
      committedRef.current = c;
    }
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
    }
    return c;
  }, [renderScale]);

  const redrawCommitted = useCallback(() => {
    const c = ensureCommitted();
    const ctx = c.getContext('2d');
    if (!ctx) return;
    // 裏キャンバスは透明のまま保持する。白い紙は表示側のCSS背景で見せ、
    // 書き出し時に exportPng が背景と合成する。
    renderStrokes(ctx, DRAWING_WIDTH, DRAWING_HEIGHT, strokesRef.current, renderScale);
  }, [ensureCommitted, renderScale]);

  /** 表示用キャンバスへ反映する。描画中のストロークは上に重ねる。 */
  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    // 裏キャンバスは表示キャンバスと同じ画素数なので、等倍で貼る（拡大すると滲む）
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(ensureCommitted(), 0, 0);

    const cur = drawingRef.current;
    if (cur) {
      ctx.save();
      ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      drawStroke(ctx, cur.stroke);
      ctx.restore();
    }
  }, [ensureCommitted, renderScale]);

  // 解像度が変わったらキャンバスを作り直して描き直す
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = Math.round(DRAWING_WIDTH * renderScale);
    canvas.height = Math.round(DRAWING_HEIGHT * renderScale);
    redrawCommitted();
    paint();
  }, [renderScale, redrawCommitted, paint]);

  // 拡大すると粗くなるので、拡大率に応じて解像度を上げ直す
  useEffect(() => {
    const next = renderScaleFor(zoom);
    setRenderScale((cur) => (cur === next ? cur : next));
  }, [zoom]);

  const pushHistory = useCallback(() => {
    undoRef.current.push([...strokesRef.current]);
    if (undoRef.current.length > MAX_HISTORY) undoRef.current.shift();
    redoRef.current = [];
  }, []);

  const applyStrokes = useCallback(
    (next: Stroke[]) => {
      strokesRef.current = next;
      redrawCommitted();
      paint();
      forceRender((n) => n + 1);
    },
    [redrawCommitted, paint],
  );

  const undo = useCallback(() => {
    const prev = undoRef.current.pop();
    if (!prev) return;
    redoRef.current.push([...strokesRef.current]);
    applyStrokes(prev);
  }, [applyStrokes]);

  const redo = useCallback(() => {
    const next = redoRef.current.pop();
    if (!next) return;
    undoRef.current.push([...strokesRef.current]);
    applyStrokes(next);
  }, [applyStrokes]);

  /** クライアント座標 → 論理キャンバス座標。CSS transform は矩形に反映されるため割るだけでよい。 */
  const toLogical = (e: { clientX: number; clientY: number }): [number, number] => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return [
      ((e.clientX - rect.left) / rect.width) * DRAWING_WIDTH,
      ((e.clientY - rect.top) / rect.height) * DRAWING_HEIGHT,
    ];
  };

  const pressureOf = (e: React.PointerEvent | PointerEvent): number =>
    e.pointerType === 'pen' && e.pressure > 0 ? e.pressure : 0.5;

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === 'pen') hasSeenPenRef.current = true;

    // 2本指はピンチ／パンに使う
    if (e.pointerType === 'touch') {
      pinchRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinchRef.current.size === 2) {
        const [a, b] = [...pinchRef.current.values()];
        pinchStartRef.current = {
          dist: Math.hypot(a.x - b.x, a.y - b.y),
          zoom,
          cx: (a.x + b.x) / 2,
          cy: (a.y + b.y) / 2,
          panX: pan.x,
          panY: pan.y,
        };
        drawingRef.current = null;
        paint();
        return;
      }
      // ペンを検出した後は指で描かない（パームリジェクション）
      if (hasSeenPenRef.current) return;
    }

    // 中ボタン、または Space を押しながらのドラッグでパン
    if (e.button === 1 || spaceRef.current) {
      panRef.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;

    pushHistory();
    const [x, y] = toLogical(e);
    const p = pressureOf(e);
    smoothRef.current = { x, y, p };
    drawingRef.current = {
      pointerId: e.pointerId,
      startedAt: performance.now(),
      stroke: {
        tool,
        color: tool === 'eraser' ? '#000000' : color,
        size: tool === 'eraser' ? eraserSize : penSize,
        points: [[x, y, p, 0]],
      },
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    paint();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === 'touch' && pinchRef.current.has(e.pointerId)) {
      pinchRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const start = pinchStartRef.current;
      if (start && pinchRef.current.size === 2) {
        const [a, b] = [...pinchRef.current.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (start.zoom * dist) / start.dist));
        const cx = (a.x + b.x) / 2;
        const cy = (a.y + b.y) / 2;
        setZoom(next);
        setPan({ x: start.panX + (cx - start.cx), y: start.panY + (cy - start.cy) });
        return;
      }
    }

    const panning = panRef.current;
    if (panning && panning.pointerId === e.pointerId) {
      setPan((p) => ({ x: p.x + e.clientX - panning.x, y: p.y + e.clientY - panning.y }));
      panRef.current = { ...panning, x: e.clientX, y: e.clientY };
      return;
    }

    const cur = drawingRef.current;
    if (!cur || cur.pointerId !== e.pointerId) return;

    // 高頻度サンプリングの点を取りこぼさない
    const events = typeof e.nativeEvent.getCoalescedEvents === 'function'
      ? e.nativeEvent.getCoalescedEvents()
      : [e.nativeEvent];
    const t = performance.now() - cur.startedAt;
    for (const ev of events) {
      const [rawX, rawY] = toLogical(ev);
      const rawP = pressureOf(ev);
      const prev = smoothRef.current ?? { x: rawX, y: rawY, p: rawP };

      // 一次フィルタで手ぶれとデジタイザのノイズを均す
      const x = prev.x + (rawX - prev.x) * SMOOTH;
      const y = prev.y + (rawY - prev.y) * SMOOTH;
      const p = prev.p + (rawP - prev.p) * SMOOTH;
      smoothRef.current = { x, y, p };

      // ほとんど動いていない点は捨てる。重ね描きで線がムラになるのを防ぐ
      const last = cur.stroke.points[cur.stroke.points.length - 1];
      if (Math.hypot(x - last[0], y - last[1]) < MIN_STEP) continue;

      cur.stroke.points.push([x, y, p, Math.round(t)] as StrokePoint);
    }
    paint();
  };

  const finishStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pinchRef.current.delete(e.pointerId);
    if (pinchRef.current.size < 2) pinchStartRef.current = null;
    if (panRef.current?.pointerId === e.pointerId) {
      panRef.current = null;
      return;
    }
    const cur = drawingRef.current;
    if (!cur || cur.pointerId !== e.pointerId) return;
    drawingRef.current = null;
    smoothRef.current = null;
    strokesRef.current = [...strokesRef.current, cur.stroke];
    const ctx = ensureCommitted().getContext('2d');
    if (ctx) {
      // 裏キャンバスは renderScale 倍で持っているので、同じ倍率で描き込む。
      // ここで掛け忘れると、確定した線が半分の大きさで左上にずれて残り、
      // 消しゴムも狙った場所を削らなくなる。
      ctx.save();
      ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
      drawStroke(ctx, cur.stroke);
      ctx.restore();
    }
    paint();
    forceRender((n) => n + 1);
  };

  // Space によるパン、Ctrl+ホイールによるズーム、取り消し／やり直し
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceRef.current = true;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if (e.key === 'Escape') onCancel();
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceRef.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [undo, redo, onCancel]);

  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * (e.deltaY < 0 ? 1.1 : 1 / 1.1))));
  };

  const onClear = () => {
    if (strokesRef.current.length === 0) return;
    if (!confirm('描いた内容をすべて消します。よろしいですか？')) return;
    pushHistory();
    applyStrokes([]);
  };

  const onSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const doc: StrokeDoc = { ...emptyStrokeDoc(), background: bg, strokes: strokesRef.current };
      const { blob, width, height } = await exportPng(doc);
      onDone(doc, blob, { width, height });
    } catch (err) {
      alert(err instanceof Error ? err.message : '保存に失敗しました');
      setBusy(false);
    }
  };

  const sizes = tool === 'eraser' ? ERASER_SIZES : PEN_SIZES;
  const activeSize = tool === 'eraser' ? eraserSize : penSize;
  const strokeCount = strokesRef.current.length;

  return (
    <div className="draw-root">
      <header className="draw-head">
        <button className="ghost-btn" onClick={onCancel}>
          破棄
        </button>
        <div className="draw-tools">
          <button
            className={`tool-btn tool-named${tool === 'pen' ? ' is-on' : ''}`}
            onClick={() => setTool('pen')}
            aria-label="ペン"
          >
            ✏️ <span className="tool-label">ペン</span>
          </button>
          <button
            className={`tool-btn tool-named${tool === 'eraser' ? ' is-on' : ''}`}
            onClick={() => setTool('eraser')}
            aria-label="消しゴム"
          >
            ◻︎ <span className="tool-label">消しゴム</span>
          </button>
          <span className="tool-sep" />
          {tool === 'pen' && (
            <>
              {PEN_COLORS.map((c) => (
                <button
                  key={c}
                  className={`color-btn${color === c ? ' is-on' : ''}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                  aria-label={`色 ${c}`}
                />
              ))}
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                aria-label="カスタムカラー"
              />
              <span className="tool-sep" />
            </>
          )}
          {sizes.map((s) => (
            <button
              key={s}
              className={`size-btn${activeSize === s ? ' is-on' : ''}`}
              onClick={() => (tool === 'eraser' ? setEraserSize(s) : setPenSize(s))}
              aria-label={`太さ ${s}`}
            >
              <i style={{ width: Math.min(s, 20), height: Math.min(s, 20) }} />
            </button>
          ))}
          <span className="tool-sep" />
          <button className="tool-btn" onClick={undo} disabled={undoRef.current.length === 0} aria-label="取り消し">
            ↺
          </button>
          <button className="tool-btn" onClick={redo} disabled={redoRef.current.length === 0} aria-label="やり直し">
            ↻
          </button>
          <button className="tool-btn" onClick={onClear} disabled={strokeCount === 0} aria-label="全消去">
            🗑
          </button>
          <span className="tool-sep" />
          <button className="tool-btn" onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z / 1.25))} aria-label="縮小">
            −
          </button>
          <span className="zoom-label">{Math.round(zoom * 100)}%</span>
          <button className="tool-btn" onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z * 1.25))} aria-label="拡大">
            ＋
          </button>
          <button
            className="tool-btn"
            onClick={() => {
              setZoom(1);
              setPan({ x: 0, y: 0 });
            }}
            aria-label="等倍に戻す"
          >
            ⤢
          </button>
        </div>
        <button className="primary-btn" onClick={onSave} disabled={busy}>
          {busy ? '保存中…' : '完了'}
        </button>
      </header>

      <div className="draw-stage" onWheel={onWheel}>
        <div
          className="draw-surface"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        >
          <canvas
            ref={canvasRef}
            className="draw-canvas"
            style={{ width: DRAWING_WIDTH, height: DRAWING_HEIGHT }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
            onPointerLeave={finishStroke}
          />
        </div>
      </div>

      <p className="draw-hint muted">
        ペンを検出すると指では描けなくなります（手のひら誤検知の防止）。2本指でズーム・移動、
        PCは Space＋ドラッグで移動、Ctrl＋ホイールでズームできます。
      </p>
    </div>
  );
}
